import type { FC } from 'react';
import React from 'react';
import kebabCase from 'lodash/kebabCase.js';
import { TextInput, SelectInput, Link } from '@wix/cli-ui-kit';
import { Trans, useTranslation } from '@wix/cli-i18n';
import {
  createEmbeddedScriptFolderValidation,
  useValidation,
} from '../../../dev-center/components/validations/index.js';
import type { ValidScriptPlacements, ValidScriptTypes } from './const.js';
import { SCRIPT_TYPES, SCRIPT_PLACEMENTS } from './const.js';
import { useExtensions } from '../../extensions/useExtensions.js';
import { useWixCliAppBi } from '../../../bi/index.js';
import { useWizardStep } from '../Wizard.js';

export const ChooseScriptNameStep: FC<{
  scriptName?: string;
  onSubmit: (value: string) => void;
}> = ({ onSubmit, scriptName }) => {
  const { t } = useTranslation();
  const bi = useWixCliAppBi();
  const extensionType = t(
    'generate_command.flow.embedded_script'
  ).toLowerCase();
  const { isActive, goToNextStep } = useWizardStep();

  return (
    <TextInput
      isActive={isActive}
      label={t('generate_command.embedded_script.choose_name')}
      placeholder={
        scriptName ?? t('generate_command.embedded_script.name_placeholder')
      }
      onSubmit={(value) => {
        bi.cliFlowStepAnswered({
          question: t('generate_command.embedded_script.choose_name'),
          questionKey: 'generate_command.embedded_script.choose_name',
          answer: value,
          additionalInfo: { extensionType },
        });
        onSubmit(value);
        goToNextStep();
      }}
    />
  );
};

export const ChooseScriptFolderStep: FC<{
  scriptName?: string;
  scriptFolder?: string;
  onSubmit: (scriptFolder: string) => void;
}> = ({ onSubmit, scriptFolder, scriptName }) => {
  const { t } = useTranslation();
  const bi = useWixCliAppBi();
  const extensionType = t(
    'generate_command.flow.embedded_script'
  ).toLowerCase();
  const { isActive, goToNextStep } = useWizardStep();

  const { getExtensions } = useExtensions();
  const embeddedScripts = getExtensions('EmbeddedScript');

  const defaultScriptFolder = kebabCase(scriptName);

  const validateEmbeddedScriptFolder = useValidation(
    createEmbeddedScriptFolderValidation(embeddedScripts)
  );

  return (
    <TextInput
      isActive={isActive}
      label={t('generate_command.embedded_script.choose_folder')}
      placeholder={scriptFolder ?? defaultScriptFolder}
      onSubmit={(value) => {
        bi.cliFlowStepAnswered({
          question: t('generate_command.embedded_script.choose_folder'),
          questionKey: 'generate_command.embedded_script.choose_folder',
          answer: value,
          additionalInfo: { extensionType },
        });
        onSubmit(value);
        goToNextStep();
      }}
      validate={validateEmbeddedScriptFolder}
    />
  );
};

export const ChooseScriptTypeStep: FC<{
  onSubmit: (value: ValidScriptTypes) => void;
}> = ({ onSubmit }) => {
  const { t } = useTranslation();
  const bi = useWixCliAppBi();
  const extensionType = t(
    'generate_command.flow.embedded_script'
  ).toLowerCase();
  const { isActive, goToNextStep } = useWizardStep();

  return (
    <SelectInput<ValidScriptTypes>
      isActive={isActive}
      label={t('generate_command.embedded_script.choose_type')}
      info={
        <Trans
          i18nKey="generate_command.embedded_script.type_info"
          components={[
            // https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/wix-cli/supported-extensions/site-extensions/embedded-scripts/embedded-script-extension-files-and-code
            <Link url="https://wix.to/0GEdYOs" />,
          ]}
        />
      }
      options={SCRIPT_TYPES}
      onSubmit={(option) => {
        bi.cliFlowStepAnswered({
          question: t('generate_command.embedded_script.choose_type'),
          questionKey: 'generate_command.embedded_script.choose_type',
          answer: option.title,
          additionalInfo: { extensionType },
        });
        onSubmit(option.value);
        goToNextStep();
      }}
    />
  );
};

export const ChooseScriptPlacementStep: FC<{
  onSubmit: (value: ValidScriptPlacements) => void;
}> = ({ onSubmit }) => {
  const { t } = useTranslation();
  const bi = useWixCliAppBi();
  const extensionType = t(
    'generate_command.flow.embedded_script'
  ).toLowerCase();
  const { isActive, goToNextStep } = useWizardStep();

  return (
    <SelectInput<ValidScriptPlacements>
      isActive={isActive}
      label={t('generate_command.embedded_script.choose_placement')}
      info={t('generate_command.embedded_script.placement_info')}
      options={SCRIPT_PLACEMENTS}
      onSubmit={(option) => {
        bi.cliFlowStepAnswered({
          question: t('generate_command.embedded_script.choose_placement'),
          questionKey: 'generate_command.embedded_script.choose_placement',
          answer: option.title,
          additionalInfo: { extensionType },
        });
        onSubmit(option.value);
        goToNextStep();
      }}
    />
  );
};
